import { Injectable } from '@angular/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { Module } from 'app/library/module/models/module.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { BehaviorSubject, race } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
    FetchAllCourseOfferingExecutionsVersions,
    FetchAllCourseOfferingExecutionsVersionsFailure,
    FetchAllCourseOfferingExecutionsVersionsSuccess,
    FetchCourseOfferingExecutionsSearch,
    FetchCourseOfferingExecutionsSearchFailure,
    FetchCourseOfferingExecutionsSearchSuccess,
    FetchProgramExecutionCourseOfferingExecutions,
    FetchProgramExecutionCourseOfferingExecutionsFailure,
    FetchProgramExecutionCourseOfferingExecutionsSuccess,
} from '../actions';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';

@Injectable()
export class CourseOfferingExecutionsService {
    private _selectedVersions = new BehaviorSubject<{ version: string }[]>([]);

    selectedVersions$ = this._selectedVersions.asObservable();

    get selectedVersions(): { version: string }[] {
        return this._selectedVersions.value;
    }

    tagCtrl = new FormControl(this.selectedVersions);

    private _versionsSubject = new BehaviorSubject<{ version: string }[]>(null);
    public versionsSubject$ = this._versionsSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    versionSelected(event: MatAutocompleteSelectedEvent | MatSelectChange): void {
        if (event instanceof MatAutocompleteSelectedEvent) {
            const tag = event.option.value;
            if (!this.selectedVersions.some((v) => v.version === tag.version)) {
                this._selectedVersions.next([...this.selectedVersions, tag]);
                this.tagCtrl.setValue(null);
            }
        } else if (event instanceof MatSelectChange) {
            const selected = event.value as { version: string }[];
            this._selectedVersions.next(selected);
            this.tagCtrl.setValue(selected, { emitEvent: false });
        }
    }

    removeVersionTag(version: string): void {
        this._selectedVersions.next(this.selectedVersions.filter((tag) => tag.version !== version));
    }

    resetVersionTags(): void {
        this._selectedVersions.next([]);
    }

    async fetchCourseOfferingExecutions(courseOfferingId: string, programExecutionId: string, tags?: number[], searchTerm?: string, users?: string[]): Promise<Module[]> {
        this._store.dispatch(new FetchCourseOfferingExecutionsSearch({ courseOfferingId, programExecutionId, tags, searchTerm, users }));

        return race(
            this._action$.pipe(
                filter((courseOfferingExecutions) => !!courseOfferingExecutions),
                ofActionDispatched(FetchCourseOfferingExecutionsSearchSuccess),
                map((action: FetchCourseOfferingExecutionsSearchSuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchCourseOfferingExecutionsSearchFailure),
                map((action: FetchCourseOfferingExecutionsSearchFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchProgramExecutionCourseOfferingExecutions(
        courseOfferingId: string,
        programExecutionId: string,
        scope: number,
        search: string,
        pageSize?: number,
        pageNumber?: number,
        statusesIds?: string[],
        context?: string
    ): Promise<boolean> {
        this._store.dispatch(
            new FetchProgramExecutionCourseOfferingExecutions({
                courseOfferingId: courseOfferingId,
                programExecutionId: programExecutionId,
                scope,
                search,
                pageSize,
                pageNumber,
                statusesIds,
                context,
            })
        );

        return await this._actionHelper.race(FetchProgramExecutionCourseOfferingExecutionsSuccess, FetchProgramExecutionCourseOfferingExecutionsFailure).toPromise();
    }

    async fetchCourseOfferingExecutionsVersions(): Promise<Module[]> {
        this._store.dispatch(new FetchAllCourseOfferingExecutionsVersions());

        return race(
            this._action$.pipe(
                filter((courseOfferingExecutions) => !!courseOfferingExecutions),
                ofActionDispatched(FetchAllCourseOfferingExecutionsVersionsSuccess),
                map((action: FetchAllCourseOfferingExecutionsVersionsSuccess) => {
                    this._versionsSubject.next(action.payload);
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchAllCourseOfferingExecutionsVersionsFailure),
                map((action: FetchAllCourseOfferingExecutionsVersionsFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }
}
